<template>
  <div>
    <!-- ── BUILDER MODE (formTemplate === false) ── -->
    <template v-if="!formTemplate">
      <div class="fh fw center" v-if="loading">
        <loading-circle />
      </div>

      <div v-else class="table-template">
        <!-- Header -->
        <div class="table-template-header v-center space-between px-4">
          <div class="v-center">
            <img @click="goBack" class="mr-5" src="~@/assets/images/icons/arrow-back.svg" width="20px" />
            <h1 class="weight-500 l">{{ headerTitle }}</h1>
          </div>
          <div class="v-center" v-if="user.tenantLevelRole === 'ADMIN'">
            <div v-if="editChecklist">
              <button class="btn btn-black mx-1" @click="enableEdit">Edit</button>
            </div>
            <div v-else>
              <button v-if="checklistId" class="btn btn-cancel mx-1" @click="cancelEdit">Cancel</button>
              <button @click="saveTemplate" class="btn mr-5" :disabled="!isValidTemplate">
                💾 {{ saveButtonText }}
              </button>
            </div>
          </div>
        </div>

        <!-- Main Layout -->
        <div class="template-builder-layout">
          <div class="template-main">
            <div class="template-config">
              <div class="config-section">
                <div class="visual-data-section">
                  <h2 class="template-title">Quality Control Inspection</h2>
                  <div class="job-order-line">
                    <span class="label-text">Job Order :</span>
                    <span class="value-text">--</span>
                  </div>
                  <div class="job-info-section">
                    <h4 class="section-heading">Checklist Information</h4>
                    <div class="info-grid">
                      <div class="info-item"><label>Part Name</label><div>--</div></div>
                      <div class="info-item"><label>Part Number</label><div>--</div></div>
                      <div class="info-item"><label>Operation</label><div>--</div></div>
                      <div class="info-item"><label>Created By</label><div>--</div></div>
                      <div class="info-item"><label>Created On</label><div>--</div></div>
                      <div class="info-item"><label>Updated By</label><div>--</div></div>
                      <div class="info-item"><label>Updated On</label><div>--</div></div>
                    </div>
                    <div class="inspector-status-row">
                      <div class="info-item">
                        <label>Inspector Name</label>
                        <input type="text" class="disabled-value" disabled value=" --" />
                      </div>
                      <div class="info-item">
                        <label>Status</label>
                        <div class="status-buttons">
                          <button class="status-btn active" disabled>In Progress</button>
                          <button class="status-btn approved" disabled>Approved</button>
                          <button class="status-btn rejected" disabled>Rejected</button>
                          <button class="status-btn hold" disabled>Hold</button>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                <div class="form-group">
                  <label>Template Name</label>
                  <input
                    :disabled="editChecklist"
                    v-model="templateName"
                    type="text"
                    class="form-control"
                    placeholder="Enter template name"
                  />
                </div>

                <div class="form-group">
                  <label>Description</label>
                  <textarea
                    :disabled="editChecklist"
                    v-model="templateDescription"
                    class="form-control"
                    placeholder="Enter template description"
                    rows="3"
                  ></textarea>
                </div>
              </div>
              <!-- Builder table instance — only rendered in builder mode -->
              <advanced-table-creator
                ref="builderTableCreator"
                :editChecklist="editChecklist"
                :initialRows="builderInitialRows"
                :initialColumnDataTypes="builderInitialColumnDataTypes"
                :initialColumnRequired="builderInitialColumnRequired"
                :initialColumnParentCustomList="builderColumnParentCustomList"
                :customFormFields="customFormFields"
                @table-data-updated="onBuilderTableDataUpdated"
                @validation-error="onValidationError"
              />
            </div>
          </div>
        </div>
      </div>
    </template>

    <template v-if="formTemplate">
      <div v-if="data" :class="{ 'form-input': true, 'form-input--required': data.required }">
        <label class="form-input__label" v-show="data.caption">
         <span class="form-input__caption">{{ data.caption }}</span>
          <FormVisibilityIcon
          v-if="data.visibility"
          class="form-input__visibility"
          v-tooltip="'Field is visible in formlist page'"
          color="#00A800"
        />
        </label>
      </div>
      <div class="tableList" :class="{ 'tableList--embedded': formTemplate }">
        <advanced-table-creator
          ref="formTableCreator"
          :formTemplate="formTemplate"
          :isFormMode="isFormMode"
          :mode="mode"
          :previewMode="previewMode"
          :data="data"
          :formColumnDefinitions="resolvedFormColumns"
          :initialRows="formInitialRows"
          :initialColumnDataTypes="formInitialColumnDataTypes"
          :initialColumnRequired="formInitialColumnRequired"
          :initialColumnParentCustomList="formColumnParentCustomList"
          :customFormFields="customFormFields"
          @table-data-updated="onFormTableDataUpdated"
          @validation-error="onValidationError"
        />
      </div>
    </template>
  </div>
</template>

<script>
import { createForm, getFormTypeForChecklist, getCheckListById, updateForm, getFormFields } from '@/api'
import { mapGetters } from 'vuex'
import loadingCircle from '@/components/common/loadingCircle.vue'
import { alert, success } from '@/plugins/notification'
import AdvancedTableCreator from '@/components/template/advancedTableCreator.vue'
import { getDuplicateValidationMessage, hasDuplicateValidationError } from '@/utils/duplicateValidation'
import FormVisibilityIcon from '@/components/common/icons/formVisibilityIcon.vue'
import config from '@/config'

const DATA_TYPE_MAP = {
  text: 'TEXT',
  number: 'NUMBER',
  date: 'DATE',
  boolean: 'BOOLEAN',
  configuration_list: 'CONFIGURATION_LIST'
}

export default {
  name: 'table-template',
  components: { AdvancedTableCreator, loadingCircle, FormVisibilityIcon },

  props: {
    formTemplate: { type: Boolean, default: false },
    isFormMode: { type: Boolean, default: false },
    data: { type: Object, default: () => ({}) },
    mode: { type: String, default: '' },
    formData: { type: Object, default: null },
    templateId: { type: String, default: null },
    fieldId: { type: [String, Number], default: null },
    value: { type: [Object, Array], default: null },
    previewMode: { type: Boolean, default: false },
    formTypeId: { type: [Number, String], default: null }
  },

  data () {
    return {
      loading: false,
      defaultFields: [],
      templateName: '',
      templateDescription: '',
      checklistId: null,
      editChecklist: false,
      builderRows: [],
      builderColumnDataTypes: [],
      builderColumnRequired: [],
      builderInitialRows: null,
      builderInitialColumnDataTypes: null,
      builderInitialColumnRequired: null,
      builderColumnParentCustomList: {},
      formRows: [],
      formColumnDataTypes: [],
      formColumnRequired: [],
      formInitialRows: null,
      formInitialColumnDataTypes: null,
      formInitialColumnRequired: null,
      formColumnParentCustomList: {},
      customFormFields: []
    }
  },

  computed: {
    ...mapGetters(['user']),
    isValidTemplate () {
      return this.templateName.trim() !== '' && this.builderRows.length > 0
    },
    headerTitle () {
      return this.checklistId ? 'Edit Table Template' : 'Create New Table Template'
    },
    saveButtonText () {
      return this.checklistId ? 'Update' : 'Save'
    },
    resolvedFormTableData () {
      if (this.data?.table_data?.columns?.length) {
        return this.data.table_data
      }

      if (this.value && !Array.isArray(this.value) && this.value.columns?.length) {
        return this.value
      }

      const fieldId = this.fieldId || this.data?.field_id
      if (fieldId && Array.isArray(this.formData?.forms_table)) {
        const matchedTable = this.formData.forms_table.find(t => String(t.field_id) === String(fieldId))
        if (matchedTable?.table_data?.columns?.length) {
          return matchedTable.table_data
        }
      }
      return null
    },
    resolvedFormColumns () {
      const tableData = this.resolvedFormTableData
      if (!Array.isArray(tableData?.columns) || !tableData.columns.length) return []
      return this.applyColumnEditability(tableData.columns, tableData.rows || [])
    }
  },

  created () {
    if (this.formTemplate) {
      this.initializeFormMode()
    } else {
      this.initializeBuilderMode()
    }
  },

  mounted () {
    this.loadCustomFields()
  },
  watch: {
    value: {
      immediate: false,
      handler (val) {
        if (!this.formTemplate) return
        if (val && this.componentValue && JSON.stringify(val) === JSON.stringify(this.componentValue)) return
        this.initializeFormMode()
      }
    },
    'data.table_data': {
      immediate: true,
      handler (val) {
        if (!this.formTemplate || !val) return
        if (this.componentValue && JSON.stringify(val) === JSON.stringify(this.componentValue)) return
        this.initializeFormMode()
      }
    }
  },
  methods: {
    async initializeBuilderMode () {
      this.loading = true
      try {
        this.checklistId = this.getChecklistIdFromRoute()
        if (this.checklistId) {
          await this.fetchBuilderData()
        }
        await this.loadDefaultFields()
        this.editChecklist = !!this.checklistId
      } catch (error) {
        console.error('Builder init error:', error)
        alert('Failed to initialize component')
      } finally {
        this.loading = false
      }
    },

    getChecklistIdFromRoute () {
      const id = this.$route.params.id || this.$route.params.templateId || this.templateId
      return id === 'new' ? null : id
    },

    async fetchBuilderData () {
      const response = await getCheckListById(this.checklistId)
      const checklistData = response.core_form_templates_by_pk
      if (!checklistData?.template_versions) return
      const activeVersion = checklistData.template_versions.find(v => v.active)
      if (!activeVersion?.template_fields) return

      const tableField = activeVersion.template_fields.find(
        f => f.field_name === 'Table' || f?.form_field?.key === 'TABLE' || f.field_type_id === 20
      )
      if (!tableField?.table_data) return

      this.templateName = checklistData.name || ''
      this.templateDescription = checklistData.description || ''

      const transformed = this.reverseTransformTableData(tableField.table_data)
      if (transformed) {
        this.builderInitialRows = JSON.parse(JSON.stringify(transformed.rows))
        this.builderInitialColumnDataTypes = [...transformed.columnDataTypes]
        this.builderInitialColumnRequired = [...(transformed.columnRequired || [])]
        this.builderColumnParentCustomList = { ...transformed.columnParentCustomList }
        this.builderRows = JSON.parse(JSON.stringify(transformed.rows))
        this.builderColumnDataTypes = [...transformed.columnDataTypes]
        this.builderColumnRequired = [...(transformed.columnRequired || [])]
      }
    },

    async loadDefaultFields () {
      try {
        const response = await getFormTypeForChecklist()
        const targetFormType = response.core_form_types.find(item => item.id === 16)
        this.defaultFields = targetFormType?.default_fields?.map(field => ({
          ...field,
          field_name: this.toSnakeCase(field.field_name)
        })) || []
      } catch (error) {
        console.error('Error loading default fields:', error)
      }
    },

    async enableEdit () {
      await this.initializeBuilderMode()
      this.editChecklist = false
    },

    async cancelEdit () {
      await this.initializeBuilderMode()
      this.editChecklist = true
    },

    goBack () {
      this.$router.go(-1)
    },

    onBuilderTableDataUpdated (tableData) {
      this.builderRows = tableData.rows
      this.builderColumnDataTypes = tableData.columnDataTypes
      this.builderColumnRequired = tableData.columnRequired || []
      this.builderInitialColumnRequired = [...this.builderColumnRequired]
      this.builderColumnParentCustomList = tableData.columnParentCustomList || {}
    },

    async saveTemplate () {
      if (!this.templateName.trim()) {
        alert('Please enter a template name')
        return
      }

      const tableCreator = this.$refs.builderTableCreator
      if (!tableCreator) {
        alert('Insert table')
        return
      }

      const validation = await tableCreator.validateTable()
      if (!validation.isValid) {
        validation.errors.forEach(e => alert(e))
        return
      }

      const tableData = tableCreator.getTableData()
      this.builderRows = tableData.rows
      this.builderColumnDataTypes = tableData.columnDataTypes
      this.builderColumnRequired = tableData.columnRequired || []
      this.builderColumnParentCustomList = tableData.columnParentCustomList || {}

      if (!this.builderRows.length || this.builderRows.length < 2) {
        alert('Add at least one row to the table')
        return
      }

      const uniq = this.validateUniqueColumnNames(this.builderRows[0])
      if (!uniq.valid) { alert(uniq.message); return }

      if (!this.validateColumnConsistency(this.builderRows, this.builderColumnDataTypes)) return

      await this.performSave()
    },
    async saveFormTemplate (options = {}) {
      const tableCreator = this.$refs.formTableCreator
      if (!tableCreator) return false

      const validation = await tableCreator.validateTable(options)
      if (!validation.isValid) {
        validation.errors.forEach(e => alert(e))
        return false
      }

      const tableData = tableCreator.getTableData()
      this.formRows = tableData.rows
      this.formColumnDataTypes = tableData.columnDataTypes
      this.formColumnRequired = tableData.columnRequired || []
      this.formColumnParentCustomList = tableData.columnParentCustomList || {}

      // Update componentValue so submitForm can read it
      const transformed = this.transformTableDataToApiFormat(
        this.formRows,
        this.formColumnDataTypes,
        this.formColumnParentCustomList,
        this.formColumnRequired
      )
      this.componentValue = this.mode === 'TEMPLATE'
        ? transformed
        : this.formData?.forms_table?.length
          ? { update: transformed }
          : transformed

      const uniq = this.validateUniqueColumnNames(this.formRows[0])
      if (!uniq.valid) {
        alert(uniq.message)
        return false
      }

      if (!this.isFormMode && !this.validateColumnConsistency(this.formRows, this.formColumnDataTypes)) return false

      return true
    },
    async performSave () {
      this.loading = true
      try {
        const transformedTable = this.transformTableDataToApiFormat(
          this.builderRows,
          this.builderColumnDataTypes,
          this.builderColumnParentCustomList,
          this.builderColumnRequired
        )
        const fields = JSON.parse(JSON.stringify(this.defaultFields))
        fields.forEach(field => {
          if (field.field_name === 'table' || field.field_name === 'Table') {
            field.field_name = 'table'
            field.table_data = transformedTable
          }
        })

        if (!this.checklistId) {
          await createForm(fields, 16, this.templateName, undefined, undefined, this.templateDescription)
          success('Table template saved successfully!')
        } else {
          await updateForm(this.checklistId, fields, this.templateName, undefined, undefined, this.templateDescription)
          success('Table template updated successfully!')
        }
        this.$router.push('/settings/checklist/list')
      } catch (error) {
        if (hasDuplicateValidationError(error)) {
          alert(getDuplicateValidationMessage('Checklist'))
        } else {
          alert('Failed to save template')
        }
      } finally {
        this.loading = false
      }
    },
    async initializeFormMode () {
      try {
        if (this.value && !Array.isArray(this.value) && this.value.columns && this.value.rows) {
          const transformed = this.reverseTransformTableData(this.value)
          if (transformed) {
            this.formInitialRows = JSON.parse(JSON.stringify(transformed.rows))
            this.formInitialColumnDataTypes = [...transformed.columnDataTypes]
            this.formInitialColumnRequired = [...(transformed.columnRequired || [])]
            this.formColumnParentCustomList = { ...transformed.columnParentCustomList }
            this.formRows = JSON.parse(JSON.stringify(transformed.rows))
            this.formColumnDataTypes = [...transformed.columnDataTypes]
            this.formColumnRequired = [...(transformed.columnRequired || [])]
          }
          return
        }

        if (this.formData?.forms_table?.length) {
          const fieldId = this.fieldId || this.data?.field_id

          const matchedTable = fieldId
            ? this.formData.forms_table.find(t => String(t.field_id) === String(fieldId))
            : null
          const tableData = matchedTable?.table_data || this.data?.table_data
          if (tableData) {
            const transformed = this.reverseTransformTableData(tableData)
            if (transformed) {
              this.formInitialRows = JSON.parse(JSON.stringify(transformed.rows))
              this.formInitialColumnDataTypes = [...transformed.columnDataTypes]
              this.formInitialColumnRequired = [...(transformed.columnRequired || [])]
              this.formColumnParentCustomList = { ...transformed.columnParentCustomList }
              this.formRows = JSON.parse(JSON.stringify(transformed.rows))
              this.formColumnDataTypes = [...transformed.columnDataTypes]
              this.formColumnRequired = [...(transformed.columnRequired || [])]
            }
            return
          }
        }
        if (this.data?.table_data) {
          const transformed = this.reverseTransformTableData(this.data.table_data)
          if (transformed) {
            this.formInitialRows = JSON.parse(JSON.stringify(transformed.rows))
            this.formInitialColumnDataTypes = [...transformed.columnDataTypes]
            this.formInitialColumnRequired = [...(transformed.columnRequired || [])]
            this.formColumnParentCustomList = { ...transformed.columnParentCustomList }
            this.formRows = JSON.parse(JSON.stringify(transformed.rows))
            this.formColumnDataTypes = [...transformed.columnDataTypes]
            this.formColumnRequired = [...(transformed.columnRequired || [])]
          }
          return
        }
        const templateId = this.$route.params.id ||
                       this.$route.params.templateId ||
                       this.templateId

        if (!templateId || templateId === 'new') return

        const response = await getCheckListById(templateId)
        const checklistData = response.core_form_templates_by_pk
        if (!checklistData?.template_versions) return

        const activeVersion = checklistData.template_versions.find(v => v.active)
        if (!activeVersion?.template_fields) return

        const fieldId = this.fieldId || this.data?.field_id
        const tableField = fieldId
          ? activeVersion.template_fields.find(f => f.field_id === fieldId)
          : activeVersion.template_fields.find(
            f => f.field_name === 'Table' ||
               f?.form_field?.key === 'TABLE' ||
               f.field_type_id === 20
          )
        if (!tableField?.table_data) return

        const transformed = this.reverseTransformTableData(tableField.table_data)
        if (!transformed) return

        this.formInitialRows = JSON.parse(JSON.stringify(transformed.rows))
        this.formInitialColumnDataTypes = [...transformed.columnDataTypes]
        this.formInitialColumnRequired = [...(transformed.columnRequired || [])]
        this.formColumnParentCustomList = { ...transformed.columnParentCustomList }
        this.formRows = JSON.parse(JSON.stringify(transformed.rows))
        this.formColumnDataTypes = [...transformed.columnDataTypes]
        this.formColumnRequired = [...(transformed.columnRequired || [])]
      } catch (error) {
        console.error('Form mode init error:', error)
      }
    },

    onFormTableDataUpdated (tableData) {
      this.formRows = tableData.rows
      this.formColumnDataTypes = tableData.columnDataTypes
      this.formColumnRequired = tableData.columnRequired || []
      this.formInitialColumnRequired = [...this.formColumnRequired]

      const componentValue = this.transformTableDataToApiFormat(
        tableData.rows,
        tableData.columnDataTypes,
        tableData.columnParentCustomList,
        tableData.columnRequired,
        { preserveEmptyHeaders: true }
      )
      this.formColumnParentCustomList = tableData.columnParentCustomList || {}

      const currentFormTypeId = this.formTypeId || this.formData?.form_type_id
      const isStandardBOMForm = currentFormTypeId === config.STANDARD_BOM_FORM.form_type

      const shouldWrap =
        !isStandardBOMForm &&
        this.mode !== 'TEMPLATE' &&
        this.formData?.forms_table?.length

      this.componentValue = shouldWrap ? { update: componentValue } : componentValue

      const fieldId = this.fieldId || this.$props?.data?.field_id

      this.$emit('customChange', {
        field_id: fieldId,
        value: this.componentValue
      })
    },

    onValidationError (error) {
      alert(error)
    },
    async loadCustomFields () {
      try {
        const res = await getFormFields()
        if (Array.isArray(res.core_form_fields)) {
          const allowedKeys = ['text', 'number', 'date', 'boolean', 'configuration_list']
          this.customFormFields = res.core_form_fields
            .filter(f => f.enabled && allowedKeys.includes(f.key.toLowerCase()))
            .map(f => ({ caption: f.caption, id: f.id, key: f.key }))
        }
      } catch (error) {
        console.error('Error loading custom fields:', error)
      }
    },

    toSnakeCase (str) {
      if (!str) return str
      return str
        .replace(/([A-Z])/g, '_$1')
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, '_')
        .replace(/^_+|_+$/g, '')
    },

    reverseTransformTableData (tableData) {
      if (!tableData?.columns || !tableData?.rows) return null
      const { columns, rows: apiRows } = tableData

      const columnDataTypes = columns.map(col => {
        const key = (col?.type ? String(col.type).trim() : '').toLowerCase()
        return DATA_TYPE_MAP[key] || 'TEXT'
      })
      const columnRequired = columns.map(col => Boolean(col?.required ?? col?.is_required ?? false))
      const columnParentCustomList = {}
      columns.forEach((col, index) => {
        if ((col?.type || '') === 'CONFIGURATION_LIST' && col?.custom_list_id) {
          columnParentCustomList[index] = {
            id: col.custom_list_id,
            name: col.custom_list_name || ''
          }
        }
      })

      const headerRow = {
        id: 'row-header',
        cells: columns.map((col, i) => ({
          id: `cell-header-${i + 1}`,
          content: col.label
        }))
      }

      const dataRows = apiRows.map((apiRow, rIdx) => ({
        id: `row-${rIdx + 2}`,
        cells: columns.map((col, cIdx) => ({
          id: `cell-${rIdx + 2}-${cIdx + 1}`,
          content: this.formatCellContent(apiRow[col.id], col.type)
        }))
      }))

      return { rows: [headerRow, ...dataRows], columnDataTypes, columnParentCustomList, columnRequired }
    },

    formatCellContent (content, type) {
      switch ((type || '').toLowerCase()) {
      case 'configuration_list':
        return Array.isArray(content) ? content : []
      case 'boolean':
      case 'bool':
        if (content === null || content === undefined || content === '') return null
        if (content === true || content === 'true' || content === 1 || content === '1') return true
        if (content === false || content === 'false' || content === 0 || content === '0') return false
        return null
      case 'number':
      case 'num':
        return (content === null || content === undefined || content === '') ? '' : content
      case 'date':
        return content || ''
      default:
        return content || ''
      }
    },
    isSerializedCellFilled (content, type) {
      switch ((type || '').toLowerCase()) {
      case 'configuration_list':
        return Array.isArray(content) && content.length > 0
      case 'boolean':
      case 'bool':
        return typeof content === 'boolean'
      case 'number':
      case 'num':
        return content !== null && content !== undefined && content !== ''
      default:
        return content !== null && content !== undefined && content.toString().trim() !== ''
      }
    },

    applyColumnEditability (columns, rows = []) {
      const hasRows = Array.isArray(rows) && rows.length > 0

      return columns.map(column => {
        if (!hasRows) {
          return {
            ...column,
            editable: true
          }
        }

        const hasValues = rows.some(row => this.isSerializedCellFilled(row?.[column.id], column?.type))
        return {
          ...column,
          editable: !hasValues
        }
      })
    },

    transformTableDataToApiFormat (tableRows, columnDataTypes, columnParentCustomList = {}, columnRequiredOrOptions = [], maybeOptions = {}) {
      const headerRow = tableRows[0]
      const usedIds = new Set()
      const dataRows = tableRows.slice(1)
      const hasNoDataRows = dataRows.length === 0
      const columnRequired = Array.isArray(columnRequiredOrOptions) ? columnRequiredOrOptions : []
      const options = (!Array.isArray(columnRequiredOrOptions) && typeof columnRequiredOrOptions === 'object')
        ? columnRequiredOrOptions
        : (maybeOptions || {})
      const preserveEmptyHeaders = options.preserveEmptyHeaders === true

      const columns = headerRow.cells.map((cell, i) => {
        const fallbackLabel = `Column ${i + 1}`
        const rawLabel = (cell?.content || '').toString().trim()
        const safeLabel = rawLabel || fallbackLabel
        const sourceColumn = this.isFormMode ? (this.resolvedFormColumns[i] || {}) : null
        let colId = sourceColumn?.id || safeLabel.toLowerCase().trim()
          .replace(/[^a-z0-9]+/g, '_')
          .replace(/^_+|_+$/g, '') || `column_${i + 1}`
        let suffix = 2
        while (usedIds.has(colId)) { colId = `${colId}_${suffix}`; suffix++ }
        usedIds.add(colId)

        const hasData = dataRows.some(row => {
          const c = row.cells[i]?.content
          if (Array.isArray(c)) return c.length > 0
          return typeof c === 'boolean' ||
            (c !== null && c !== undefined && c.toString().trim() !== '')
        })
        const normalizedType = columnDataTypes[i] || sourceColumn?.type || 'TEXT'
        const editable = hasNoDataRows ? true : !hasData
        const isColRequired = Array.isArray(columnRequired) && columnRequired.length > i
          ? Boolean(columnRequired[i])
          : Boolean(sourceColumn?.required ?? sourceColumn?.is_required ?? false)

        const column = this.isFormMode
          ? {
            ...sourceColumn,
            id: colId,
            label: preserveEmptyHeaders ? rawLabel : safeLabel,
            type: normalizedType,
            required: isColRequired,
            editable
          }
          : {
            id: colId,
            label: preserveEmptyHeaders ? rawLabel : safeLabel,
            type: normalizedType,
            required: isColRequired,
            editable
          }
        if (normalizedType === 'CONFIGURATION_LIST') {
          const parentCustomList = columnParentCustomList[i]
          if (parentCustomList?.id) {
            column.custom_list_id = parentCustomList.id
            column.custom_list_name = parentCustomList.name || ''
            column.use_abbreviation = false
          }
        }
        return column
      })

      const rows = dataRows.map((row, rIdx) => {
        const rowData = { id: `row_${rIdx + 1}` }
        row.cells.forEach((cell, cIdx) => {
          rowData[columns[cIdx].id] = this.convertCellValue(cell.content, columnDataTypes[cIdx])
        })
        return rowData
      })

      return { version: 1, columns, rows }
    },

    convertCellValue (content, dataType) {
      switch ((dataType || '').toLowerCase()) {
      case 'CONFIGURATION_LIST':
        if (!Array.isArray(content) || content.length === 0) return []
        return content.map(tag => ({
          custom_list_value_id: tag.custom_list_value_id,
          custom_list_value_name: tag.custom_list_value_name
        }))
      case 'number':
        return (content !== null && content !== undefined && content !== '')
          ? parseFloat(content) : null
      case 'boolean':
        if (content === null || content === undefined || content === '') return null
        if (content === true || content === 'true' || content === 1 || content === '1') return true
        if (content === false || content === 'false' || content === 0 || content === '0') return false
        return null
      case 'date':
        return content || null
      default:
        return content || null
      }
    },

    validateUniqueColumnNames (headerRow) {
      if (!headerRow?.cells) return { valid: true }
      const seen = new Map()
      for (let i = 0; i < headerRow.cells.length; i++) {
        const n = (headerRow.cells[i].content || '').toString().trim() || `Column ${i + 1}`
        const key = n.toLowerCase()
        seen.set(key, (seen.get(key) || 0) + 1)
        if (seen.get(key) > 1) {
          return { valid: false, message: `Duplicate column name "${n}". Column names must be unique.` }
        }
      }
      return { valid: true }
    },

    isCellEmpty (content) {
      if (content === null || content === undefined) return true
      if (typeof content === 'boolean' || typeof content === 'number') return false
      return content.toString().trim() === ''
    },

    validateColumnConsistency (rows, columnDataTypes = this.formColumnDataTypes) {
      if (rows.length <= 1) return true
      const headerRow = rows[0]
      const dataRows = rows.slice(1)
      for (let colIndex = 0; colIndex < headerRow.cells.length; colIndex++) {
        const columnName = headerRow.cells[colIndex].content
        const dataType = columnDataTypes[colIndex]
        let filledCount = 0
        let emptyCount = 0
        dataRows.forEach(row => {
          const content = row.cells[colIndex]?.content
          if (dataType === 'CONFIGURATION_LIST') {
            const isEmpty = !content || (Array.isArray(content) && content.length === 0)
            isEmpty ? emptyCount++ : filledCount++
          } else {
            this.isCellEmpty(content) ? emptyCount++ : filledCount++
          }
        })
        if (filledCount > 0 && emptyCount > 0) {
          alert(`Column "${columnName}" has incomplete data. Either fill all ${dataRows.length} rows or leave all rows empty.`)
          return false
        }
      }
      return true
    }
  }
}
</script>

<style lang="scss" scoped>
.table-template {
  height: 100%;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.table-template-header {
  background: #f8f9fa;
  border-bottom: 1px solid #dee2e6;
  padding: 1rem 0;
  margin-bottom: 1rem;
  flex-shrink: 0;
}

.template-builder-layout {
  display: flex;
  flex: 1;
  overflow: hidden;
  min-height: 0;
}

.template-sidebar {
  width: 250px;
  flex-shrink: 0;
  background: #f8f9fa;
  border-right: 1px solid #dee2e6;
}

.template-main {
  flex: 1;
  display: flex;
  flex-direction: column;
  overflow-y: auto;
  overflow-x: hidden;
  padding-right: 1rem;
}

.template-config {
  background: #f8f9fa;
  border: 1px solid #e9ecef;
  border-radius: 8px;
  padding: 1.5rem;
  margin-bottom: 1.5rem;
  flex-shrink: 0;
}

.config-section {
  h3 {
    margin-bottom: 1rem;
    color: #495057;
  }
}

.form-group {
  margin-bottom: 1rem;

  label {
    display: block;
    margin-bottom: 0.5rem;
    font-weight: 500;
    color: #495057;
  }

  .form-control {
    width: 100%;
    padding: 0.5rem;
    border: 1px solid #ced4da;
    border-radius: 4px;
    font-size: var(--text-base);

    &.error {
      border-color: #dc3545;
    }
  }
}

.table-builder {
  background: #fff;
  border: 1px solid #dee2e6;
  border-radius: 8px;
  padding: 1.5rem;
}

.builder-header {
  margin-bottom: 1.5rem;
  padding-bottom: 1rem;
  border-bottom: 1px solid #dee2e6;
}

.columns-config {
  margin-bottom: 2rem;

  h4 {
    margin-bottom: 1rem;
    color: #495057;
  }
}

.column-item {
  background: #f8f9fa;
  border: 1px solid #dee2e6;
  border-radius: 6px;
  padding: 1rem;
  margin-bottom: 1rem;
}

.column-inputs {
  display: grid;
  grid-template-columns: 2fr 1fr 1fr auto;
  gap: 1rem;
  align-items: center;
}

.column-actions {
  display: flex;
  gap: 0.5rem;
}

.dropdown-options {
  margin-top: 1rem;

  label {
    display: block;
    margin-bottom: 0.5rem;
    font-size: var(--text-base);
    color: #6c757d;
  }
}

.table-preview {
  h4 {
    margin-bottom: 1rem;
    color: #495057;
  }
}

.preview-container {
  border: 1px solid #dee2e6;
  border-radius: 6px;
  overflow: hidden;
}

.preview-table {
  width: 100%;
  border-collapse: collapse;

  th,
  td {
    border: 1px solid #dee2e6;
    padding: 0.5rem;
    text-align: left;
  }

  th {
    background: #f8f9fa;
    font-weight: 600;
    color: #495057;
  }

  td {
    background: #fff;
  }

  .form-control {
    border: none;
    padding: 0.25rem;
    font-size: var(--text-base);
    width: 100%;

    &:focus {
      outline: 2px solid #007bff;
      outline-offset: -2px;
    }
  }
}

.no-data {
  text-align: center;
  padding: 2rem;
  color: #6c757d;
  font-style: italic;
}

.info-item {
  .disabled-value {
    padding: 0.5rem;
    background-color: #f8f9fa;
    border: 1px solid #dee2e6;
    border-radius: 4px;
    color: #6c757d;
    font-size: var(--text-base);
  }
}

.status-btn {
  &.approved {
    background: transparent;
    color: #6c757d;
    border-color: #dee2e6;

    &:not(.active) {
      background-color: #dcfce7;
      color: #166534;
      border: 1px solid #bbf7d0;
    }
  }

  &.rejected {
    background: transparent;
    color: #6c757d;
    border-color: #dee2e6;

    &:not(.active) {
      background-color: #fef2f2;
      color: #991b1b;
      border: 1px solid #fecaca;
    }
  }

  &.hold {
    background: transparent;
    color: #6c757d;
    border-color: #dee2e6;

    &:not(.active) {
      background-color: #fefce8;
      color: #a16207;
      border: 1px solid #dee2e6;
    }
  }
}

.v-center {
  display: flex;
  align-items: center;
}

.space-between {
  justify-content: space-between;
}

.px-5 {
  padding-right: 3rem;
}

.mr-3 {
  margin-right: 1rem;
}

.mr-5 {
  margin-right: 0.5rem;
}

.weight-500 {
  font-weight: 500;
}

.xl {
  font-size: 1.5rem;
}

/* ---------- Visual Data Section ---------- */

.visual-data-section {
  margin-bottom: 2rem;
  padding-bottom: 2rem;
  border-bottom: 2px solid #dee2e6;
}

.template-title {
  font-size: 1.5rem;
  font-weight: 600;
  color: #212529;
  margin-bottom: 1rem;
}

.job-order-line {
  margin-bottom: 1.5rem;
  font-size: var(--text-base);

  .label-text {
    font-weight: 500;
    color: #495057;
    margin-right: 0.5rem;
  }

  .value-text {
    color: #6c757d;
  }
}

.job-info-section {
  background: #fff;
  border: 1px solid #dee2e6;
  border-radius: 6px;
  padding: 1.5rem;

  .section-heading {
    font-size: 1rem;
    font-weight: 600;
    color: #495057;
    margin-bottom: 1rem;
  }
}

.info-grid {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 1rem;
  margin-bottom: 1rem;
}

.info-item {
  label {
    display: block;
    margin-bottom: 0.5rem;
    font-weight: 500;
    font-size: var(--text-base);
    color: #495057;
  }

  .form-control:disabled {
    background-color: #e9ecef;
    opacity: 1;
    border-radius: 6px;
    cursor: not-allowed;
  }
}

.inspector-status-row {
  display: grid;
  grid-template-columns: 1fr 2fr;
  gap: 1rem;
  align-items: end;
}

.status-buttons {
  display: flex;
  gap: 0.5rem;
}

.status-btn {
  padding: 0.5rem 1rem;
  border: 1px solid #dee2e6;
  border-radius: 4px;
  background: white;
  color: #6c757d;
  font-size: var(--text-base);
  font-weight: 500;
  transition: all 0.2s;

  &.active {
    background-color: #fefce8;
    color: #ff7b00;
    border: 1px solid #edc7a3;
  }

  &:disabled {
    opacity: 1;
  }
}

.tableList {
    flex: 1;
    min-width: 150px;
    // max-width: calc(100vw - 400px);
    padding: 10px;
    // background-color: #f5f5f5;
    border: 1px solid #ddd;
    border-radius: 6px;
    font-size: var(--text-base);
    color: #777;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    max-height: calc(100vh - 300px);
    overflow-y: auto;
}

.tableList--embedded {
    overflow: visible;
    overflow-y: visible;
    max-height: none;
}
</style>
